export type SupademoVariables = {
  email?: string;
  name?: string;
  custom_variable?: string;
  [key: string]: string | undefined;
};

export type SupademoInitOptions = {
  variables?: SupademoVariables;
  [key: string]: unknown;
};

export type SupademoOpenOptions = {
  type?: 'demo' | 'showcase';
  [key: string]: unknown;
};

export interface SupademoSDK {
  (key: string, options?: SupademoInitOptions): void;
  open: (id: string, options?: SupademoOpenOptions) => void;
}

declare global {
  interface Window {
    Supademo?: SupademoSDK;
  }
}

const SUPADEMO_SCRIPT_ID = 'supademo-sdk';
const SUPADEMO_SCRIPT_SRC = 'https://script.supademo.com/supademo.js';

let currentKey: string | null = null;
let isInitializing = false;
let isUnavailable = false;

function markSupademoUnavailable(error: unknown): void {
  isUnavailable = true;
  currentKey = null;
  console.debug('Supademo unavailable', error);
}

function loadSupademoScript(): Promise<boolean> {
  return new Promise((resolve) => {
    if (window.Supademo) {
      resolve(true);

      return;
    }

    if (isUnavailable) {
      resolve(false);

      return;
    }

    const existingScript = document.getElementById(SUPADEMO_SCRIPT_ID) as HTMLScriptElement | null;

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(true), { once: true });
      existingScript.addEventListener(
        'error',
        (event) => {
          markSupademoUnavailable(event);
          existingScript.remove();
          resolve(false);
        },
        { once: true },
      );

      return;
    }

    const script = document.createElement('script');
    script.id = SUPADEMO_SCRIPT_ID;
    script.src = SUPADEMO_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = (event) => {
      markSupademoUnavailable(event);
      script.remove();
      resolve(false);
    };

    document.body.appendChild(script);
  });
}

export async function initSupademo(key: string, options?: SupademoInitOptions): Promise<void> {
  if (!key || isInitializing || isUnavailable) return;

  currentKey = key;
  isInitializing = true;

  try {
    const loaded = await loadSupademoScript();

    if (!loaded || !window.Supademo) {
      currentKey = null;

      return;
    }

    window.Supademo(key, options);
  } catch (err) {
    markSupademoUnavailable(err);
  } finally {
    isInitializing = false;
  }
}

export function updateSupademoUser(variables?: SupademoVariables): void {
  if (!currentKey || !window.Supademo) return;

  try {
    window.Supademo(currentKey, {
      variables: variables ?? {},
    });
  } catch (err) {
    markSupademoUnavailable(err);
  }
}

export function startDemo(id: string): void {
  if (!window.Supademo) return;

  try {
    window.Supademo.open(id);
  } catch (err) {
    markSupademoUnavailable(err);
  }
}

export function startShowcase(id: string): void {
  if (!window.Supademo) return;

  try {
    window.Supademo.open(id, { type: 'showcase' });
  } catch (err) {
    markSupademoUnavailable(err);
  }
}
